import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface MarketingTemplate {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface UpsertTemplate {
  name: string;
  subject: string;
  bodyHtml: string;
}

export interface MarketingRecipient {
  customerId: string;
  displayName: string;
  email: string | null;
  eligible: boolean;
  exclusionReason: string | null; // "no_email" | "unsubscribed" | null
}

export interface MarketingEligibility {
  enabled: boolean;
  addressOnFile: boolean;
  dailyCap: number;
  usedToday: number;
  remaining: number;
  recipients: MarketingRecipient[];
}

export interface MarketingExcluded {
  customerId: string;
  displayName: string;
  reason: string;
}

export interface MarketingSendResult {
  sendId: string;
  recipientCount: number;
  remainingToday: number;
  excluded: MarketingExcluded[];
}

export function useMarketingTemplates() {
  return useQuery({
    queryKey: ['marketing', 'templates'],
    queryFn: async () => (await apiClient.get<MarketingTemplate[]>('/api/v1/marketing/templates')).data,
    staleTime: 30_000
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id?: string; body: UpsertTemplate }) => {
      const res = p.id
        ? await apiClient.put<MarketingTemplate>(`/api/v1/marketing/templates/${p.id}`, p.body)
        : await apiClient.post<MarketingTemplate>('/api/v1/marketing/templates', p.body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing', 'templates'] })
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/marketing/templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing', 'templates'] })
  });
}

export function useMarketingEligibility(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'eligibility'],
    queryFn: async () => (await apiClient.get<MarketingEligibility>('/api/v1/marketing/eligibility')).data,
    staleTime: 15_000,
    enabled
  });
}

export function useSendMarketing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { templateId: string; customerIds: string[] }) =>
      (await apiClient.post<MarketingSendResult>('/api/v1/marketing/send', p)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing', 'eligibility'] })
  });
}
